import { useEffect, useState, useCallback } from 'react';
import { Row, Col, message } from 'antd';
import sortBy from 'lodash/sortBy';
import { getLangList } from '@/lib/i18n';
import AttendanceConfig from "@/config/attendance";
import ResponsiveTable from '@/components/responsiveTable';
import { ReButton } from "@/components/refineElement";
import { convertToTimestamp, startTimeToday, endTimeToday, convertTimestampToFormat, dateTimeFormat } from "@/lib/dateTime";
import { usePrevious } from '@/hooks';
import { hasPermission } from "@/lib/permissionUtil";
import { showCheckConfirm } from "@/lib/antdElementFunction";
import { StudentType } from './types';

const { getSignInfo, signIn, cancelSignIn } = AttendanceConfig;

interface Props {
  studentArray: StudentType[];
}

function StudentsRollCall(props: Props) {
  const { studentArray } = props;
  const [stateStudentArray, setStateStudentArray] = useState<StudentType[]>([]);
  const prevStudentArray = usePrevious([...studentArray]);
  const studentListT: any = getLangList("studentList");
  const errorT: any = getLangList("error");
  const buttonT: any = getLangList("button");

  const columns = [
    {
      dataIndex: "whetherRollCall",
      render: (text: boolean, record: any) => {
        const { leaveType, signTime } = record;
        return (
          <ReButton
            onClick={changeRollCall(record, signTime)}
            attributeObject={{
              disabled: !hasPermission('ATTENDANCE_EDIT') || leaveType,
              size: 'large',
              type: signTime ? 'dashed' : 'primary',
            }}
          >
            {signTime ? buttonT.CANCEL : studentListT.SIGN}
          </ReButton>
        )
      },
    },
    { title: studentListT.CN_NAME, dataIndex: "nameCn" },
    { title: studentListT.EN_NAME, dataIndex: "nameEn" },
    {
      title: studentListT.TIME,
      dataIndex: "signTime",
      render: (text: any) => text ? convertTimestampToFormat(text, dateTimeFormat) : '',
    },
  ];

  const getSignInfoFunction = useCallback(() => {
    const params = {
      startTime: convertToTimestamp(startTimeToday),
      endTime: convertToTimestamp(endTimeToday),
    };

    getSignInfo(params).then((res: any) => {
      const todayStudentDataArray = res?.value ?? [];
      const newStudentArray = studentArray.map((classItem: any) => (
        todayStudentDataArray.find((todayItem: any) => todayItem.username === classItem.username) ?? { ...classItem, signTime: null }
      ));
      setStateStudentArray(sortBy(newStudentArray, (item) => item.cramSeat));
    });
  }, [studentArray]);

  useEffect(() => {
    if (JSON.stringify(studentArray) !== JSON.stringify(prevStudentArray)) {
      getSignInfoFunction();
    }
  }, [studentArray, prevStudentArray, getSignInfoFunction]);

  const changeRollCall = (record: any, signTime: any) => () => {
    const { username } = record;
    signTime
      ? showCheckConfirm(
        () => cancelSignInFunction(username),
        buttonT,
        buttonT.CONFIRM_CANCEL,
      )
      : signInFunction(username);
  }

  const signInFunction = (username: string) => {
    const params = { username };

    signIn(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      getSignInfoFunction();
    });
  }

  const cancelSignInFunction = (username: string) => {
    const params = { username };

    cancelSignIn(params).then((res: any) => {
      if (!res.success) {
        return message.error(errorT.failed);
      }
      message.success(errorT.success);
      getSignInfoFunction();
    });
  }

  return (
    <Row gutter={[0, 8]}>
      <Col span={24}>
        <ResponsiveTable
          dataSource={stateStudentArray}
          columns={columns}
          rowClassName={(record: any) => record.leaveType ? 'mark' : ''}
          displayScrollY={false}
        />
      </Col>
    </Row>
  );
}

export default StudentsRollCall;